export const MUTINY_LOCAL_STORAGE_KEY = 'mutiny';

export const IS_MUTINY: string =
  typeof localStorage !== 'undefined' && localStorage.getItem(MUTINY_LOCAL_STORAGE_KEY) === 'false' ? 'false' : 'true';

export const CACHE_PREFIX: 'o-api' | 'p-api' = IS_MUTINY === 'true' ? 'o-api' : 'p-api';

export const APP: string = import.meta.env.VITE_APP || 'myaccount.ui';
export const WSS: string = import.meta.env.VITE_WSS || 'wss://wss.test.p2g.ninja';
export const VERSION: string = import.meta.env.VITE_VERSION || '0.0.0';
export const ENVIRONMENT: string = import.meta.env.VITE_ENVIRONMENT || '0.0.0';
export const CONTENT_API: string = import.meta.env.VITE_CONTENT_API_ORIGIN || '';
export const TRACKING_EDGE_API: string = import.meta.env.VITE_TRACKING_EDGE_API || 'https://api.global.test.p2g.ninja';
export const QUERY_API: string = import.meta.env.VITE_QUERY_API || 'https://query.test.p2g.ninja';
export const SELFSERVE_API: string = import.meta.env.VITE_SELFSERVE_API || 'https://selfserve-edge.test.p2g.ninja';
export const RULEMANAGER_API: string = import.meta.env.VITE_RULEMANAGER_API || 'https://rule-manager.test.p2g.ninja';
export const MY_API: string = import.meta.env.VITE_MY_API || '';
export const ACCOUNTLINKING_API: string =
  import.meta.env.VITE_ACCOUNTLINKING_API || 'https://accountlinking.test.p2g.ninja';
export const SMARTSEND_API: string = import.meta.env.VITE_SMARTSEND_API || '';
export const QUOTE_API: string = import.meta.env.VITE_QUOTE_API || '';
export const ORDERPROCESS_EDGE_API: string = import.meta.env.VITE_ORDERPROCESS_EDGE_API || '';
export const LABEL_API: string = import.meta.env.VITE_LABEL_API || '';
export const PAYMENT_PATH: string = import.meta.env.VITE_PAYMENT_PATH || '';
// Order-process (checkout) app paths the consumer experience deep-links into for Buy Again / Arrange
// Return. Same-origin paths; sensible defaults so they resolve even if the env var is unset.
export const BUYAGAIN_PATH: string = import.meta.env.VITE_BUYAGAIN_PATH || '/order-process/buy-again';
export const RETURN_PATH: string = import.meta.env.VITE_RETURN_PATH || '/order-process/return';
// ui-checkout's embeddable add-a-card route, framed by the Stored Cards page. Same shape as the two
// above - a path into the order-process app, resolved against the current host - but framed rather
// than navigated to, so the customer never leaves My Account.
export const ADD_CARD_PATH: string = import.meta.env.VITE_ADD_CARD_PATH || '/order-process/saved-cards/add';
/**
 * A DIFFERENT origin to load the add-a-card frame from - the local-development escape hatch, empty
 * everywhere else.
 *
 * In production both apps sit behind the storefront proxy, so the frame is same-origin and this is
 * unset. Running My Account on `localhost:3000` there is no order-process app underneath it, so the
 * frame has to come from a deployed checkout - and that is cross-origin, which changes two things:
 * the src cannot be sanitised to the current host, and inbound messages arrive from there rather
 * than from us.
 *
 * ui-checkout already has the matching half (`?hostOrigin=` checked against its own build-time
 * `VITE_EMBED_ALLOWED_HOST_ORIGINS`, refused outright in a production build), so this is the host
 * side of a workflow that already exists rather than a new hole. Honoured only when the My Account
 * build is itself non-production - see `add-card.tsx`.
 */
export const ADD_CARD_ORIGIN: string = import.meta.env.VITE_ADD_CARD_ORIGIN || '';
export const SUBSCRIPTIONS_PATH: string = import.meta.env.VITE_SUBSCRIPTIONS_PATH || '/order-process/subscriptions';
export const SUBSCRIPTIONS_CHECKOUT_PATH: string = `${SUBSCRIPTIONS_PATH}/checkout`;
export const ANALYTICS_EDGE_API: string =
  import.meta.env.VITE_ANALYTICS_EDGE_API || 'https://api.global.test.p2g.ninja';
export const PAYMENT_API: string = import.meta.env.VITE_PAYMENT_API || '';
export const GTM_ENABLED: boolean = import.meta.env.VITE_GTM_ENABLED === 'true';
export const LOCAL_TENANT_OVERRIDE: string | null = import.meta.env.VITE_LOCAL_TENANT_OVERRIDE || null;
export const DEBOUNCE_TIME: number = parseInt(import.meta.env.VITE_DEBOUNCE_TIME || '750', 10);
export const LOCALES_PATH: string =
  import.meta.env.VITE_PUBLIC_LOCALES_PATH || 'https://apps.backoffice.parcel2go.com/~/.p2g/locales/';

export const AWS_REGION: string = import.meta.env.VITE_AWS_REGION || 'eu-west-1';

export const AWS_ACCESS_KEY: string = import.meta.env.VITE_AWS_ACCESS_KEY_ID || '';

export const AWS_ACCESS_SECRET: string = import.meta.env.VITE_AWS_SECRET_ACCESS_KEY || '';
export const INVOICE_API: string = import.meta.env.VITE_INVOICE_API || '';

export const TranslationNamespaces = {
  SELFSERVE: 'selfserve',
  MYACCOUNT: 'myaccount',
  COMMON: 'common',
  COUNTRY: 'country',
  STATEMENTS: 'statements',
  MYORDERS: 'myorders',
  MYDETAILS: 'mydetails',
  PREPAY: 'prepay',
  STORED_CARDS: 'stored-cards',
  SETTINGS: 'user-settings',
  REWARDS: 'rewards',
  SURCHARGES: 'surcharges',
  COMMUNICATION_PREFERENCES: 'communication-preferences',
  ACCOUNT_LINKING: 'accounts',
  API_CLIENTS: 'api-clients',
  SAVED_ADDRESSES: 'addresses',
  SMARTSEND: 'smartsend',
  RULEMANAGER: 'rulemanager',
  SUPPORT_TICKET: 'support',
  CLAIMS: 'claims',
  CHECKOUT: 'checkout'
};

/**
 * Namespaces loaded up front, on every route.
 *
 * Keep this to what the chrome genuinely needs. Each namespace costs a request per language on
 * every cold page load, and a non-English tenant also pulls the English fallback - so this list
 * is the main lever on our locize download volume. Everything else is declared per route via
 * `handle.namespaces` in `main.tsx` and prefetched by `useNamespaces`; anything missed there is
 * still loaded on demand by `useTranslation`, just slightly later.
 *
 * `myaccount` is here because the navigation chrome (`routes/layouts/my-account/*`) renders on
 * every page. The first entry also becomes i18next's `defaultNS`.
 */
export const CORE_TRANSLATION_NAMESPACES = [TranslationNamespaces.MYACCOUNT];

export const ContentGroups = {
  SELFSERVE: 'SelfServe',
  REWARDS: 'Rewards',
  MYACCOUNT: 'MyAccount',
  MYORDERS: 'MyOrders',
  STATEMENTS: 'Statements',
  MYDETAILS: 'MyDetails',
  PREPAY: 'PrePay',
  STORED_CARDS: 'StoredCards',
  // Content shown inside side panels that open over any route, so it is fetched on its own rather
  // than through the route-scoped ContentContext. See src/hooks/use-cms-content-group.ts.
  PAYMENTS: 'Payments',
  BILLING: 'Billing',
  SETTINGS: 'UserSettings',
  SURCHARGES: 'Surcharges',
  COMMUNICATION_PREFERENCES: 'CommunicationPreferences',
  ACCOUNT_LINKING: 'Accounts',
  API_CLIENTS: 'ApiClients',
  SAVED_ADDRESSES: 'Addresses',
  SMARTSEND: 'SmartSend',
  RULEMANAGER: 'RuleManager',
  SUPPORT_TICKET: 'support',
  CLAIMS: 'claims',
  SUBSCRIPTIONS: 'Subscriptions'
};
